import React, { useEffect, useState } from 'react'
import NewModal from 'components/modal/NewModal'
import { userRolesBase } from 'model/User'
import { getDoctorsForPatients } from 'user/UserApi'
import { useTranslation } from 'react-i18next'
import 'antd/dist/antd.css'
import { Label } from '@hospitalrun/components'
import classes from '../../../../style.module.scss'
import SelectWithLabelFormGroup from 'components/input/SelectWithLableFormGroup'
import {
  bloodChemistry,
  cardiacMarkers,
  deviceOrder,
  Devices,
  edanVitals,
  rapidCovidFlu,
  rapidCovidFluOrderLabel,
  rapidCovidFluOrderOptions,
} from 'model/Devices'
import { ComponentWrapperWithAITracking } from 'analytics/AppInsights'
import { Col, Radio, Row, InputNumber, Checkbox, Alert, Select, Button, Form } from 'antd'
import MedicalTestSearch from 'patients/medical-tests/MedicalTestSearch'
import '../../../VdgPatients.scss'
import Patient, { patientStatus } from 'model/Patient'
import Device from 'model/Device'
import PatientsDiagnosisTopics from 'model/PatientsDiagnosisTopics'
import MedicalTest from 'model/MedicalTest'
import VdgMedicalTestReferralModal from './VdgMedicalTestReferralModal'
import { toWithIn, WITHIN_OPTIONS, withInLabel } from './withInOptions'
import {
  cancelPatientSchedulePlan,
  closePatientSchedulePlan,
  createPatientSchedulePlan,
  editPatientSchedulePlan,
  fetchPatientExaminations,
} from 'vdg-patients/vdg-patients-diagnosis-slice'
import { useDispatch, useSelector } from 'react-redux'
import ReactSelectWithLabelFormGroup from 'components/input/ReactSelectWithLabelFormGroup'
import { RootState } from 'store'
// import TextAreaWithLabelFormGroup from 'components/input/TextAreaWithLabelFormGroup'
import DatePickerWithLabelFormGroup from 'components/input/DatePickerWithLabelFormGroup'

import './VdgTestModal.scss'
import PatientDiagnosisRepository from 'vdg-patients/appointment/PatientDiagnosisRepository'
import InternalExaminationPlan, { OrderSource } from 'model/InternalExaminationPlan'
import moment from 'moment'
import TextAreaWithLabelFormGroup from 'components/input/TextAreaWithLabelFormGroup'
import PatientsDiagnosisDiscussions from 'model/PatientsDiagnosisDiscussions'
import LockerWrapper from 'patients/view/LockerWrapper'
import {
  useDeviceAvailability,
  unavailableLabel,
} from 'settings/deviceIncidents/useDeviceAvailability'
import { ExternalEmr } from 'utils/ExternalEmr'
import TextArea from 'antd/lib/input/TextArea'
import { returnExamName, withBloodTypes } from 'model/Examination'
import OrderReasonFields, {
  isOrderReasonComplete,
  OrderDiagnosis,
  withoutAnchor,
} from 'vdg-patients/common/OrderReasonFields'

const { externalEMR } = (window as any).config

interface OutpatientModalProps {
  show: boolean
  parent: string
  onCloseButtonClick: () => void
  patient: Patient
  // Optional in fact: VdgExaminationResults declares `topic?: any` and passes it
  // straight through, and a patient with no diagnosis topics has none to give.
  // Declared required until now, which nothing caught — ComponentWrapperWithAITracking
  // wraps this as withAITracking<any>, so props are not type-checked at any call site.
  topic?: PatientsDiagnosisTopics
  existingPlan?: InternalExaminationPlan
  onFieldChange?: (key: string, value: string | boolean | any[]) => void
}

export enum typeTestDef {
  internal = 'internal',
  external = 'external',
}

export enum scheduleTypeDef {
  once = 'once',
  multi = 'multi',
  every = 'every',
  continuous = 'continuous',
}

export enum severityTypeDef {
  onceADay = 'onceADay',
  onceAWeek = 'onceAWeek',
  onceTwoWeeks = 'onceTwoWeeks',
}

// A "times a day" plan repeats every N days (1 = every day). Labels: workspace.schedule.repeat.<N>.
export const REPEAT_EVERY_X_DAYS_OPTIONS = [1, 2, 3, 7, 14, 28]
// How far ahead a plan may end: a daily plan keeps its short horizon, an interval plan reaches
// further (the server enforces the 60-day cap too).
const MAX_DAILY_PLAN_DAYS_AHEAD = 5
const MAX_RECURRING_PLAN_DAYS_AHEAD = 60

/** The old plan's next test day on or after today, so an edit keeps the plan's rhythm. */
const nextOccurrence = (plan: InternalExaminationPlan): string => {
  const today = moment().startOf('day')
  const everyXDays = plan.everyXDays && plan.everyXDays > 1 ? plan.everyXDays : 1
  const day = moment(plan.startDate ?? plan.createdAt).startOf('day')
  if (!day.isValid()) return today.toISOString()
  while (day < today) day.add(everyXDays, 'days')
  return day.toISOString()
}

// Analyzer-fed exams whose order names what to run on the tube: Piccolo reagent discs, PATHFAST
// cartridges. The selection travels as the plan's bloodTypes onto every exam it creates.
const BLOOD_TYPE_DEVICES = [bloodChemistry, cardiacMarkers]

const bloodTypesLabelKey = (device: Device) =>
  device.value === cardiacMarkers.value
    ? 'examinations.pathfastAssays'
    : 'examinations.picoloBloodTypes'

const VdgTestModal = (props: OutpatientModalProps) => {
  const { show, onCloseButtonClick, patient, parent, topic, existingPlan } = props
  const { userSession } = useSelector((state: RootState) => state.userSession)

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { Option } = Select

  // Devices out of service at this patient's facility (open device incidents).
  const { unavailable: unavailableDevices } = useDeviceAvailability(patient?.facilityId)

  const [tests, setTests] = useState<any[]>([])
  const [testName, setTestName] = useState<Device>()
  const [testType, setTestType] = useState<typeTestDef>(typeTestDef.internal)
  const [showMedicalTestReferralModal, setShowMedicalTestReferralModal] = useState<boolean>(false)
  const [medicalTest, setMedicalTest] = useState<MedicalTest>()
  const [allDoctors, setAllDoctors] = useState<any[]>([])
  const [prescribingDoctor, setPrescribingDoctor] = useState<any>()
  const [showAlertModal, setShowAlertModal] = useState(false)
  const { diagnosis } = useSelector((state: RootState) => state.patientDiagnosis)
  // A new order starts with no codes: the doctor picks the reason for this test, rather than
  // inheriting the topic's diagnoses. The picker offers the patient's diagnoses as quick picks.
  //
  // An edited plan keeps its codes, without the Z00.8 the server added only to anchor codes
  // picked from the full ICD-10 list.
  const [newExistsDiagnosis, setNewExistsDiagnosis] = useState<OrderDiagnosis[]>(
    existingPlan
      ? withoutAnchor(existingPlan.patientDiagnoses ?? []).map((d: any) => ({
          diagnosisCode: d.diagnosisCode,
          diagnosisId: d.diagnosisId,
          diagnosisName:
            d.diagnosisName ??
            diagnosis?.find(pd => pd.diagnosisCode === d.diagnosisCode)?.diagnosisName,
        }))
      : [],
  )
  const [indication, setIndication] = useState<string>(existingPlan?.indication ?? '')
  const [bloodTypes, setBloodTypes] = useState<any[]>([
    ...(existingPlan?.bloodTypes ? existingPlan.bloodTypes : []),
  ])
  const bloodTypesDevice = BLOOD_TYPE_DEVICES.find(d => d.value === testName?.value)
  const [scheduleType, setScheduleType] = useState<scheduleTypeDef>()

  const [multiDateStart, setMultiDateStart] = useState<string>(
    moment().startOf('day').toISOString(),
  )
  const [multiDateEnd, setMultiDateEnd] = useState<string>('')
  const [repeatEveryXDays, setRepeatEveryXDays] = useState<number>(1)
  const [everyDateEnd, setEveryDateEnd] = useState<string>('')
  const [continuousDateEnd, setContinuousDateEnd] = useState<string>('')

  const [withIn, setWithIn] = useState<number | 'ASAP'>(0)
  const [timesaday, setTimesaday] = useState<number>(1)
  const [every, setEvery] = useState<number>(1)
  const [severity, setSevirity] = useState<severityTypeDef>(severityTypeDef.onceADay)
  const [noticeMessage, setNoticeMessage] = useState<string>()
  const [doctorsRequests, setDoctorsRequests] = useState<string | undefined>(
    existingPlan?.additionalRequests,
  )

  const [comments, setComments] = useState<string>()
  const [closeExistingPlan, setCloseExistingPlan] = useState<boolean>(false)

  const severities = [
    { label: t('examinations.dailymonitoring'), value: severityTypeDef.onceADay },
    { label: t('examinations.weeklymonitoring'), value: severityTypeDef.onceAWeek },
    { label: t('examinations.oncefor2weeks'), value: severityTypeDef.onceTwoWeeks },
  ]

  useEffect(() => {
    let tmpTest: any[] = Devices
    let newTests: any[] = []
    tmpTest.map((test: any) => {
      if (test.value === rapidCovidFlu.value) {
        // One kit, three orderable options (see rapidCovidFluOrderOptions).
        rapidCovidFluOrderOptions.forEach(option => {
          newTests.push({ ...test, orderedTests: option.orderedTests })
        })
      } else if (test.kind === 'device') {
        if (test.types.length > 1) {
          test.types.map((type: any) => {
            newTests.push({ ...test, ...{ types: [type] } })
          })
        } else {
          newTests.push(test)
        }
      } else {
        //order
        newTests.push(test)
      }
    })
    setTests(newTests)
  }, [])

  useEffect(() => {
    if (testName?.value !== edanVitals.value && scheduleType === scheduleTypeDef.continuous) {
      setScheduleType(undefined)
    }
  }, [testName])

  useEffect(() => {
    if (testName) {
      PatientDiagnosisRepository.checkIfScheduleExists({ patientId: patient.id, testName }).then(
        res => {
          if (res.data.examPlanExists) {
            const { examPlans } = res.data
            setNoticeMessage(t('examinations.thereIsAnExistingPlanAlready'))

            examPlans?.forEach((examPlan: any) => {
              setNewExistsDiagnosis([
                ...newExistsDiagnosis,
                ...examPlan?.patientDiagnoses?.filter(
                  ({ diagnosisCode }: any) =>
                    !newExistsDiagnosis.find(f => f.diagnosisCode === diagnosisCode),
                ),
              ])
              if (BLOOD_TYPE_DEVICES.some(d => d.value === testName.value))
                setBloodTypes(Array.from(new Set([...bloodTypes, ...examPlan?.bloodTypes])))
              if (examPlan.scheduleType === scheduleTypeDef.multi) {
                setTimesaday(examPlan.timesADay)
                setRepeatEveryXDays(examPlan.everyXDays || 1)
                setMultiDateEnd(examPlan.endPlanDate)
              } else if (examPlan.scheduleType === scheduleTypeDef.every) {
                setEvery(examPlan.everyXHours)
                setEveryDateEnd(examPlan.endPlanDate)
              } else if (examPlan.scheduleType === scheduleTypeDef.continuous) {
                setSevirity(examPlan.severity)
                setContinuousDateEnd(examPlan.endPlanDate)
              }
            })
          } else {
            setNoticeMessage(undefined)
          }
        },
      )
    }
  }, [testName, scheduleType])

  // Only the doctors who treat this patient may be named as the prescriber —
  // the server enforces the same set when the order is saved.
  useEffect(() => {
    if (!patient?.id) {
      setAllDoctors([])
      return
    }

    let cancelled = false
    getDoctorsForPatients([patient.id])
      .then(res => {
        if (cancelled) return
        const ids: string[] = res.data.byPatient?.[patient.id] ?? []
        const byId = new Map<string, any>(
          (res.data.doctors ?? []).map((doctor: any) => [doctor._id, doctor]),
        )
        setAllDoctors(ids.map(id => byId.get(id)).filter(Boolean))
      })
      .catch(err => {
        console.error(err)
        if (!cancelled) setAllDoctors([])
      })

    return () => {
      cancelled = true
    }
  }, [patient?.id])

  useEffect(() => {
    if (existingPlan) {
      setTestName(existingPlan.test)
      setTestType(typeTestDef.internal)

      setScheduleType(existingPlan.scheduleType)
      if (existingPlan.scheduleType === scheduleTypeDef.multi) {
        existingPlan.endPlanDate && setMultiDateEnd(existingPlan.endPlanDate)
        existingPlan.timesADay && setTimesaday(existingPlan.timesADay)
        setRepeatEveryXDays(existingPlan.everyXDays || 1)
        setMultiDateStart(nextOccurrence(existingPlan))
      } else if (existingPlan.scheduleType === scheduleTypeDef.every) {
        existingPlan.endPlanDate && setEveryDateEnd(existingPlan.endPlanDate)
        existingPlan.everyXHours && setEvery(existingPlan.everyXHours)
      } else if (existingPlan.scheduleType === scheduleTypeDef.continuous) {
        existingPlan.endPlanDate && setContinuousDateEnd(existingPlan.endPlanDate)
        existingPlan.severity && setSevirity(existingPlan.severity)
      }
    }
  }, [existingPlan])

  const onClose = () => {
    onCloseButtonClick()
  }

  // An open standing order can be edited; behind the scenes the server closes it (cancelling its
  // future, not-performed tests) and creates a new plan with the new settings.
  const isEditingPlan =
    !!existingPlan?._id &&
    !existingPlan.isClosed &&
    existingPlan.scheduleType !== scheduleTypeDef.once

  // A daily plan stays within a few days; once it repeats on an interval it may reach 60 days.
  const maxMultiDaysAhead =
    repeatEveryXDays > 1 ? MAX_RECURRING_PLAN_DAYS_AHEAD : MAX_DAILY_PLAN_DAYS_AHEAD

  useEffect(() => {
    const maxEnd = moment().add(maxMultiDaysAhead, 'days').endOf('day')
    if (multiDateEnd && moment(multiDateEnd) > maxEnd) setMultiDateEnd('')
  }, [maxMultiDaysAhead])

  const onSave = async () => {
    if (existingPlan && closeExistingPlan) {
      closePlan()
      onClose()
    } else if (isEditingPlan) {
      const examPlan = buildExamPlan()
      examPlan && existingPlan?._id && dispatch(editPatientSchedulePlan(existingPlan._id, examPlan))
      onClose()
    } else if (testType === typeTestDef.internal) {
      createPlan()
      onClose()
    } else if (testType === typeTestDef.external) {
      setShowMedicalTestReferralModal(true)
    }
  }

  const onBloodTypesChanged = async (bt: any) => {
    setBloodTypes(bt ? bt : [])
  }

  const buildExamPlan = (): InternalExaminationPlan | undefined => {
    if (!scheduleType) return

    let examPlan: InternalExaminationPlan = {
      patientId: patient.id,
      patientDiagnoses: newExistsDiagnosis,
      indication: indication.trim() || undefined,
      scheduleType: scheduleType,
      typeTest: testType,
      test: { ...testName },
      prescribedBy: prescribingDoctor,
      additionalRequests: doctorsRequests,
      orderSource: OrderSource.manual,
    }

    if (scheduleType === scheduleTypeDef.every) {
      examPlan.everyXHours = every
      examPlan.endPlanDate = everyDateEnd
    } else if (scheduleType === scheduleTypeDef.multi) {
      examPlan.timesADay = timesaday
      examPlan.everyXDays = repeatEveryXDays
      examPlan.startDate = moment(multiDateStart).startOf('day').toISOString()
      examPlan.endPlanDate = multiDateEnd
    } else if (scheduleType === scheduleTypeDef.once) {
      examPlan.withIn = withIn
    } else if (scheduleType === scheduleTypeDef.continuous) {
      examPlan.endPlanDate = continuousDateEnd
      examPlan.severity = severity
    }

    if (bloodTypesDevice) {
      examPlan.bloodTypes = bloodTypes
    }

    return examPlan
  }

  const createPlan = () => {
    const examPlan = buildExamPlan()
    if (!examPlan) return

    // The progress note (synced to PCC) with the test and its reason, as AI recommendations write.
    // Blood chemistry / PATHFAST orders name the selected discs/assays, or every one reads the same.
    const noteTestName = testName?.orderedTests
      ? rapidCovidFluOrderLabel(testName.orderedTests)
      : examPlan.bloodTypes?.length
      ? withBloodTypes(testName?.name as string, examPlan.bloodTypes)
      : returnExamName(testName?.name as string, testName?.types?.[0])
    dispatch(
      createPatientSchedulePlan(examPlan, {
        patientId: patient.id,
        tests: [{ name: noteTestName, reasoning: examPlan.indication }],
        prescribedBy: prescribingDoctor ?? null,
        patientDiagnoses: newExistsDiagnosis,
      }),
    )
    dispatch(fetchPatientExaminations(patient.id))
  }

  const closePlan = () => {
    // Cancelling an active plan takes no comment and adds nothing to the discussion; the plan still
    // records who closed it and when. Closing an ended plan keeps its required comment/follow-up.
    if (existingPlan?._id && !isExistingPlanDone(existingPlan)) {
      dispatch(cancelPatientSchedulePlan(existingPlan._id, patient.id))
      return
    }

    const followUp: PatientsDiagnosisDiscussions = {
      patientId: patient.id,
      createdAt: new Date().toISOString(),
      createdBy: userSession.id,
      schedulePlanId: existingPlan?._id,
      id: '',
      rev: '',
      updatedAt: '',
      updatedBy: '',
      role: userSession.role,
      discussion: comments as string,
      patientDiagnoses: [...(existingPlan?.patientDiagnoses ? existingPlan?.patientDiagnoses : [])],
    }

    existingPlan?._id && dispatch(closePatientSchedulePlan(existingPlan?._id, followUp))
  }

  const onChangeRadioGroup = (e: any) => {
    setTestType(e.target.value)
  }

  function onChangeTimesaday(value: any) {
    setTimesaday(value)
  }

  function onChangeEvery(value: any) {
    setEvery(value)
  }

  function onChangeOnce(value: any) {
    setWithIn(toWithIn(value))
  }

  const onChangeDoctor = (value: any) => {
    setPrescribingDoctor(value)
  }

  function disabledMultiEveryDate(current: any) {
    return (
      current &&
      (current < moment().startOf('day') || current > moment().add(5, 'days').endOf('day'))
    )
  }

  function disabledMultiStartDate(current: any) {
    return (
      current &&
      (current < moment().startOf('day') ||
        current > moment().add(MAX_RECURRING_PLAN_DAYS_AHEAD, 'days').endOf('day'))
    )
  }

  function disabledMultiEndDate(current: any) {
    const earliest = moment.max(moment().startOf('day'), moment(multiDateStart).startOf('day'))
    return (
      current &&
      (current < earliest || current > moment().add(maxMultiDaysAhead, 'days').endOf('day'))
    )
  }

  function disabledContinuousDate(current: any) {
    return (
      current &&
      (current < moment().startOf('day') || current > moment().add(6, 'months').endOf('day'))
    )
  }

  function disabledSaveButton() {
    let disabled = false
    // Closing an ended plan only needs the reason; cancelling an active one needs nothing. The
    // schedule fields are not used either way.
    if (existingPlan && closeExistingPlan) {
      return isExistingPlanDone(existingPlan) && (!comments || !(comments.trim().length > 0))
    }
    if (testType === typeTestDef.external && !medicalTest) {
      disabled = true
    } else if (testType === typeTestDef.internal) {
      const testNameMissing = !testName
      // An edited plan keeps its prescriber (the picker is locked), so only a new order needs one.
      const nurseDidntSelectDoctor =
        !existingPlan && userSession.role === userRolesBase.nurse && !prescribingDoctor
      const scheduleTypeMissing = !scheduleType
      // Missing or already past (an ended plan being renewed still shows its old end date).
      const isEndDateInvalid = (endDate: string) =>
        !endDate || moment(endDate).endOf('day') < moment()
      const multiDateEndMissing =
        scheduleType === scheduleTypeDef.multi &&
        (isEndDateInvalid(multiDateEnd) ||
          moment(multiDateEnd).endOf('day') < moment(multiDateStart).startOf('day'))
      const everyDateEndMissing =
        scheduleType === scheduleTypeDef.every && isEndDateInvalid(everyDateEnd)
      const continuousDateEndMissing =
        scheduleType === scheduleTypeDef.continuous && isEndDateInvalid(continuousDateEnd)
      const closePlanCommentsMissing =
        closeExistingPlan && (!comments || !(comments.trim().length > 0))
      const bloodTypesMissing =
        !!bloodTypesDevice && (!bloodTypes || bloodTypes.length === 0)
      // A new order needs its reason and codes; an edited plan from before the reason was required
      // may keep going without one.
      const diagnosisMissing = existingPlan
        ? newExistsDiagnosis.length === 0
        : !isOrderReasonComplete(indication, newExistsDiagnosis)

      if (
        testNameMissing ||
        nurseDidntSelectDoctor ||
        scheduleTypeMissing ||
        multiDateEndMissing ||
        everyDateEndMissing ||
        continuousDateEndMissing ||
        closePlanCommentsMissing ||
        bloodTypesMissing ||
        diagnosisMissing
      )
        disabled = true
    }

    return disabled
  }

  // Bordered box per frequency option, highlighted while it is the selected one.
  const frequencyOptionClass = (type: scheduleTypeDef) =>
    `frequencyOption${scheduleType === type ? ' frequencyOption--selected' : ''}`

  const isExistingPlanDone = (existingPlan: InternalExaminationPlan) => {
    return moment(existingPlan.endPlanDate).endOf('day') < moment()
  }
  const onCloseAlertModal = () => {
    setShowAlertModal(false)
  }

  const body = (
    <>
      {/* External test option hidden for now - not needed right now */}
      {false && !existingPlan && (
        <Row>
          <Col span={20} className="py-1">
            <div className="py-1 typeTestDef">
              <Radio.Group
                buttonStyle="solid"
                size="large"
                onChange={onChangeRadioGroup}
                value={testType}
              >
                {/* <Radio.Button value={typeTestDef.internal}>
                  {t('examination.testType.internal')}
                </Radio.Button> */}
                <Radio.Button value={typeTestDef.external} className={externalEMR ? 'hidden' : ''}>
                  {t('examination.testType.external')}
                </Radio.Button>
              </Radio.Group>
            </div>
          </Col>
          <Col span={4}>
            <Button
              className="btnExternal"
              onClick={() => {
                if (externalEMR) {
                  window.open(ExternalEmr.getUrlPatient(patient.externalId))
                }
              }}
            >
              {t('examination.testType.external')}
            </Button>
          </Col>
        </Row>
      )}

      {testType == typeTestDef.external && (
        <Row>
          <Col span={24} className="py-2">
            <div
              className={
                classes.selectTestModal +
                ' ' +
                (testType == typeTestDef.external ? '' : 'displayNone')
              }
            >
              <Label
                htmlFor="patientTypeahead"
                isRequired={true}
                text={t('scheduling.appointment.selectMedicalTest')}
              />
              <MedicalTestSearch
                onSelect={(value: any) => {
                  setMedicalTest(value.value)
                }}
              ></MedicalTestSearch>
            </div>
          </Col>
        </Row>
      )}
      {testType == typeTestDef.internal && (
        <>
          <Row>
            <Col span={24} className="py-1">
              <div className={testType == typeTestDef.internal ? '' : 'displayNone'}>
                <div className={userSession.role === userRolesBase.nurse ? '' : 'displayNone'}>
                  <LockerWrapper>
                    <Form.Item
                      label={t('patient.testprescribedBy')}
                      style={{ color: '#495e83' }}
                      // name="prescribedBy"
                      // rules={[{ required: true, message: t('required.selectPrescribingDoctor') }]}
                    >
                      <Select
                        disabled={existingPlan !== undefined}
                        showSearch
                        options={allDoctors?.map(doctor => {
                          return {
                            label: `${doctor.firstName} ${doctor.lastName}`,
                            value: doctor._id,
                          }
                        })}
                        getPopupContainer={(triggerNode: any) => {
                          return triggerNode.parentNode
                        }}
                        placeholder={`-- ${t('patient.choose')} --`}
                        onChange={onChangeDoctor}
                        filterOption={(input, option) =>
                          ((option?.label as unknown) as string)
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      />
                    </Form.Item>
                  </LockerWrapper>
                </div>
                <LockerWrapper>
                  <SelectWithLabelFormGroup
                    disabled={existingPlan !== undefined}
                    name=""
                    isRequired={true}
                    label={t('patient.testName')}
                    value={JSON.stringify(testName)}
                    isEditable={true}
                    options={tests
                      .filter(test => test.value !== deviceOrder.value && test.value != 'custom')
                      .map(test => {
                        // Out of service at this facility: kept in the list but not selectable, so
                        // the doctor can see the test exists and when it's expected back.
                        const outOfService = unavailableDevices[test.value]
                        const suffix = outOfService ? ` — ${unavailableLabel(outOfService, t)}` : ''

                        if (test.orderedTests) {
                          return {
                            label: `${rapidCovidFluOrderLabel(test.orderedTests)}${suffix}`,
                            value: JSON.stringify(test),
                            disabled: !!outOfService,
                          }
                        } else if (test.kind === 'device') {
                          return {
                            label: `${returnExamName(test.name, test.types[0])}${suffix}`,
                            value: JSON.stringify(test),
                            disabled: !!outOfService,
                          }
                        } else
                          return {
                            label: `${returnExamName(test.name)}${suffix}`,
                            value: JSON.stringify(test),
                            disabled: !!outOfService,
                          }
                      })}
                    onChange={(event: any) => {
                      setTestName(JSON.parse(event.target.value))
                    }}
                  />
                </LockerWrapper>
              </div>
            </Col>
          </Row>
          {!existingPlan && noticeMessage && (
            <Row>
              <Col span={24}>
                <Alert message={noticeMessage} type="warning" banner />
              </Col>
            </Row>
          )}
        </>
      )}
      {bloodTypesDevice?.bloodTypes && (
        <Row>
          <Col span={24} className="py-1">
            <LockerWrapper>
              <ReactSelectWithLabelFormGroup
                isRequired={true}
                name="category"
                label={t(bloodTypesLabelKey(bloodTypesDevice))}
                isMulti
                value={bloodTypes?.map((bt: any) => {
                  return {
                    label: bt,
                    value: bt,
                  }
                })}
                isEditable
                options={bloodTypesDevice.bloodTypes.map(bt => {
                  return {
                    label: bt,
                    value: bt,
                  }
                })}
                onChange={(options: any) => {
                  const formatBloodTypes: any = options?.map((option: any) => option.value)
                  onBloodTypesChanged(formatBloodTypes)
                }}
              />
            </LockerWrapper>
          </Col>
        </Row>
      )}
      <Row>
        <Col span={24} className="py-1">
          <LockerWrapper>
            <OrderReasonFields
              indication={indication}
              onIndicationChange={setIndication}
              diagnoses={newExistsDiagnosis}
              onDiagnosesChange={setNewExistsDiagnosis}
              patientDiagnoses={diagnosis}
            />
          </LockerWrapper>
        </Col>
      </Row>
      {testType == typeTestDef.internal && (
        <>
          <LockerWrapper>
            <Form layout={'vertical'}>
              <Form.Item
                label={t('patient.additionalRequestsOptional')}
                style={{ color: '#495e83', width: '100%' }}
              >
                <TextArea
                  placeholder={t('examinations.addDescription')}
                  value={doctorsRequests}
                  autoSize
                  onChange={e => setDoctorsRequests(e.target.value)}
                />
              </Form.Item>
            </Form>
          </LockerWrapper>
          <Row className="internal" gutter={[12, 12]}>
            <Col span={24}>
              <Label
                className={classes.label}
                text={t('patient.testFrequency')}
                // htmlFor={id}
                isRequired
              />
            </Col>
            {/* One box per frequency option: once | every X hours | repeat every X days. The first
                line of each box is its title: the checkbox with the option's main setting. */}
            <Col xs={24} md={8}>
              <div className={frequencyOptionClass(scheduleTypeDef.once)}>
                <div className="frequencyOption__title">
                  <Checkbox
                    disabled={existingPlan !== undefined}
                    checked={scheduleType === scheduleTypeDef.once}
                    onChange={(_event: any) => {
                      setScheduleType(scheduleTypeDef.once)
                    }}
                  >
                    <span className="text">{t('examinations.once')}</span>
                  </Checkbox>
                </div>
                <div className="frequencyOption__body">
                  <LockerWrapper>
                    <Select
                      className="w-100"
                      defaultValue={'0'}
                      onChange={onChangeOnce}
                      getPopupContainer={(triggerNode: any) => {
                        return triggerNode.parentNode
                      }}
                      disabled={scheduleType !== scheduleTypeDef.once}
                    >
                      {WITHIN_OPTIONS.map((option: any) => (
                        <Option value={option.toString()} title={option}>
                          {withInLabel(option.toString(), t)}
                        </Option>
                      ))}
                    </Select>
                  </LockerWrapper>
                </div>
              </div>
            </Col>

            <Col xs={24} md={8}>
              <div className={frequencyOptionClass(scheduleTypeDef.every)}>
                <div className="frequencyOption__title">
                  <Checkbox
                    disabled={existingPlan !== undefined}
                    className="checkbox"
                    checked={scheduleType === scheduleTypeDef.every}
                    onChange={(_event: any) => {
                      setScheduleType(scheduleTypeDef.every)
                    }}
                  ></Checkbox>
                  <span className="text">Every</span>
                  <LockerWrapper>
                    <InputNumber
                      disabled={scheduleType !== scheduleTypeDef.every}
                      min={1}
                      max={23}
                      defaultValue={every}
                      onChange={onChangeEvery}
                      value={every}
                    />
                  </LockerWrapper>
                  <span className="text">hours</span>
                </div>
                <div className="frequencyOption__body">
                  <LockerWrapper>
                    <DatePickerWithLabelFormGroup
                      className="datepicker"
                      disabled={scheduleType !== scheduleTypeDef.every}
                      value={everyDateEnd}
                      showYearDropdown={true}
                      name="examinationDate"
                      label={t('testModal.endDate')}
                      maxDate={new Date(Date.now().valueOf())}
                      onChange={(date: Date) => {
                        setEveryDateEnd(date?.toISOString())
                      }}
                      isEditable
                      disabledDate={disabledMultiEveryDate}
                      placeholder={t('testModal.selectEndDate')}
                    />
                  </LockerWrapper>
                </div>
              </div>
            </Col>

            <Col xs={24} md={8}>
              <div className={frequencyOptionClass(scheduleTypeDef.multi)}>
                <div className="frequencyOption__title">
                  <Checkbox
                    disabled={existingPlan !== undefined}
                    className="checkbox"
                    checked={scheduleType === scheduleTypeDef.multi}
                    onChange={(_event: any) => {
                      setScheduleType(scheduleTypeDef.multi)
                    }}
                  ></Checkbox>
                  <span className="text">{t('examinations.repeat')}</span>
                  <LockerWrapper>
                    <Select
                      className="repeatSelect"
                      getPopupContainer={(triggerNode: any) => {
                        return triggerNode.parentNode
                      }}
                      disabled={scheduleType !== scheduleTypeDef.multi}
                      value={repeatEveryXDays}
                      options={REPEAT_EVERY_X_DAYS_OPTIONS.map(days => ({
                        label: t(`workspace.schedule.repeat.${days}`),
                        value: days,
                      }))}
                      onChange={(value: number) => setRepeatEveryXDays(value)}
                    />
                  </LockerWrapper>
                </div>
                <div className="frequencyOption__body">
                  <div className="d-flex align-items-center pb-2">
                    <LockerWrapper>
                      <InputNumber
                        disabled={scheduleType !== scheduleTypeDef.multi}
                        min={1}
                        max={3}
                        defaultValue={timesaday}
                        onChange={onChangeTimesaday}
                        value={timesaday}
                      />
                    </LockerWrapper>
                    <span className="text mx-2">{t('examinations.timesaday')}</span>
                  </div>
                  <LockerWrapper>
                    <DatePickerWithLabelFormGroup
                      className="datepicker"
                      disabled={scheduleType !== scheduleTypeDef.multi}
                      value={multiDateStart as any}
                      name="multiStartDate"
                      label={t('testModal.startDate')}
                      onChange={(date: Date) => {
                        date && setMultiDateStart(date.toISOString())
                      }}
                      isEditable
                      allowClear={false}
                      disabledDate={disabledMultiStartDate}
                      placeholder={t('testModal.selectStartDate')}
                    />
                  </LockerWrapper>
                  <LockerWrapper>
                    <DatePickerWithLabelFormGroup
                      className="datepicker"
                      disabled={scheduleType !== scheduleTypeDef.multi}
                      value={multiDateEnd}
                      showYearDropdown={true}
                      name="examinationDate"
                      label={t('testModal.endDate')}
                      maxDate={new Date(Date.now().valueOf())}
                      onChange={(date: Date) => {
                        setMultiDateEnd(date?.toISOString())
                      }}
                      isEditable
                      disabledDate={disabledMultiEndDate}
                      placeholder={t('testModal.selectEndDate')}
                    />
                  </LockerWrapper>
                </div>
              </div>
            </Col>
            {testName?.value === edanVitals.value && (
              <Col xs={24} md={8}>
                <div className={frequencyOptionClass(scheduleTypeDef.continuous)}>
                  <div className="frequencyOption__title">
                    <Checkbox
                      disabled={existingPlan !== undefined}
                      checked={scheduleType === scheduleTypeDef.continuous}
                      onChange={(_event: any) => {
                        setScheduleType(scheduleTypeDef.continuous)
                      }}
                    >
                      <span className="text">{t('examinations.continuous')}</span>
                    </Checkbox>
                  </div>
                  <div className="frequencyOption__body">
                    <LockerWrapper>
                      <Select
                        className="w-100"
                        getPopupContainer={(triggerNode: any) => {
                          return triggerNode.parentNode
                        }}
                        disabled={scheduleType !== scheduleTypeDef.continuous}
                        value={severity}
                        options={severities}
                        onChange={(value: any) => {
                          setSevirity(value)
                        }}
                      />
                    </LockerWrapper>
                    <LockerWrapper>
                      <DatePickerWithLabelFormGroup
                        className="mt-2"
                        disabled={scheduleType !== scheduleTypeDef.continuous}
                        value={continuousDateEnd}
                        showYearDropdown={true}
                        name="examinationDate"
                        label={t('testModal.endDate')}
                        maxDate={new Date(Date.now().valueOf())}
                        onChange={(date: Date) => {
                          setContinuousDateEnd(date?.toISOString())
                        }}
                        isEditable
                        disabledDate={disabledContinuousDate}
                        placeholder={t('testModal.selectEndDate')}
                      />
                    </LockerWrapper>
                  </div>
                </div>
              </Col>
            )}
          </Row>
          {isEditingPlan && !closeExistingPlan && (
            <Row className="py-1">
              <Col span={24}>
                <Alert message={t('schedule.editPlanNotice')} type="info" showIcon />
              </Col>
            </Row>
          )}
          {existingPlan && !existingPlan.isClosed && (
            <>
              <Row>
                <LockerWrapper>
                  <Checkbox
                    className="checkbox"
                    checked={closeExistingPlan}
                    onChange={event => {
                      setCloseExistingPlan(event.target.checked)
                    }}
                  >
                    {/* An active plan is cancelled: its future, not-performed tests are removed. */}
                    {isExistingPlanDone(existingPlan)
                      ? t('schedule.closePlan')
                      : t('schedule.cancelPlan')}
                  </Checkbox>
                </LockerWrapper>
              </Row>
              {isExistingPlanDone(existingPlan) && (
                <Row>
                  <Col span={24} className="py-1">
                    <LockerWrapper>
                      <TextAreaWithLabelFormGroup
                        disabled={!closeExistingPlan}
                        name="text"
                        label={t('examinations.comments')}
                        value={comments}
                        isEditable={true}
                        placeholder={t('examinations.addDescription')}
                        onChange={(event: any) => {
                          setComments(event.target.value)
                        }}
                      />
                    </LockerWrapper>
                  </Col>
                </Row>
              )}
            </>
          )}
        </>
      )}
    </>
  )

  return (
    <>
      <NewModal
        show={show}
        title={
          isEditingPlan
            ? t('patient.appointments.editTestPlan')
            : t('patient.appointments.addTestExamination')
        }
        body={body}
        maxWidth="md"
        closeButton={{
          children: t('actions.cancel'),
          color: 'danger',
          onClick: onClose,
        }}
        successButton={{
          disabled: parent === 'view' || disabledSaveButton(),
          style: { borderWidth: 0 },
          children: t('actions.save'),
          color: 'success',
          icon: 'add',
          iconLocation: 'left',
          onClick: () => {
            patient.status === patientStatus.inactive ? setShowAlertModal(true) : onSave()
          },
        }}
        middleButton={{
          style: { borderColor: '#D8DEE9', background: 'white', color: '#495E83', borderWidth: 1 },
          children: t('actions.cancel'),
          onClick: onClose,
        }}
        className="testModal"
      />
      <NewModal
        show={showAlertModal}
        title={t('showAlertModal.title.patientInActive')}
        body={t('showAlertModal.messageBody.patientInActive')}
        closeButton={{
          children: t('actions.cancel'),
          color: 'danger',
          onClick: onCloseAlertModal,
        }}
        middleButton={{
          style: { borderColor: '#D8DEE9', background: 'white', color: '#495E83', borderWidth: 1 },
          children: t('actions.cancel'),
          onClick: onCloseAlertModal,
        }}
        successButton={{
          style: { borderWidth: 0 },
          children: t('showAlertModal.save.patientInActive'),
          color: 'success',
          iconLocation: 'left',
          onClick: onSave,
        }}
      ></NewModal>
      {showMedicalTestReferralModal && (
        <VdgMedicalTestReferralModal
          show={true}
          parent={parent}
          patient={patient}
          onCloseButtonClick={() => {
            setShowMedicalTestReferralModal(false)
            onClose()
          }}
          medicalTest={medicalTest}
          topic={topic}
        />
      )}
    </>
  )
}

export default ComponentWrapperWithAITracking(VdgTestModal, 'VdgTestModal')
